// STEP OVERLAY-CONTROL-UX-1 · 판매자 NOTICE 보관함 UI (shared).
// Studio (SellerLiveControl) · Console (SellerLiveConsole) 두 shell 에서
// 동일한 UI 로 render 되도록 presentation 통합.
//
// Props:
//   library     — useNoticeLibrary() hook 반환값
//   onApply     — (notice) => Promise · 선택 공지 를 현재 LIVE NOTICE 로 반영
//   applyLabel  — '공지 표시' (기본) — Live 진입 가능 여부에 따라 shell 이 설정
//   applyDisabled — true 면 Apply 버튼 disabled (LIVE 중이 아닐 때)
//   applyHint   — 비활성 시 안내 문구
import React, { useState } from 'react';
import { Upload, Plus, Image as ImageIcon, Trash2, Pencil } from 'lucide-react';

const NoticeLibraryCard = ({ library, onApply, applyLabel = '공지 표시',
                              applyDisabled = false, applyHint = '', hideImage = false, activeId = null }) => {
  const { items, loading, busy, uploadImage, create, update, remove } = library;
  const [draftText, setDraftText] = useState('');
  const [draftImageUrl, setDraftImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const url = await uploadImage(file);
    if (url) setDraftImageUrl(url);
    setUploading(false);
  };

  const handleCreate = async () => {
    const r = await create({ text: draftText, image_url: draftImageUrl });
    if (r) { setDraftText(''); setDraftImageUrl(''); }
  };

  const beginEdit = (n) => {
    setEditingId(n.notice_id);
    setEditText(n.text || '');
    setEditImageUrl(n.image_url || '');
  };
  const cancelEdit = () => { setEditingId(null); setEditText(''); setEditImageUrl(''); };
  const handleEditSave = async () => {
    const r = await update(editingId, { text: editText, image_url: editImageUrl });
    if (r) cancelEdit();
  };
  const handleEditUpload = async (file) => {
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setEditImageUrl(url);
  };

  return (
    <div className="space-y-2" data-testid="notice-library-card">
      <div className="rounded-lg border border-dashed border-black/15 dark:border-white/15 p-2 space-y-1.5"
           data-testid="notice-library-new-form">
        <p className="text-[10px] font-black tracking-widest uppercase text-gray-500">새 공지 만들기</p>
        <textarea value={draftText} maxLength={200} rows={2}
                  onChange={(e) => setDraftText(e.target.value)}
                  placeholder="예: 오늘 전상품 무료배송 · 사이즈표 참고"
                  className="w-full px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F] text-[12px] placeholder-gray-400 focus:outline-none focus:border-[#FF4F3D]"
                  data-testid="notice-library-new-text" />
        <div className="flex items-center flex-wrap gap-1.5">
          {draftImageUrl ? (
            <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F]"
                 data-testid="notice-library-new-image-preview">
              <img src={draftImageUrl} alt="" className="w-8 h-8 rounded object-cover" />
              <button type="button" onClick={() => setDraftImageUrl('')}
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      data-testid="notice-library-new-image-remove">제거</button>
            </div>
          ) : !hideImage && (
            <label className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border border-dashed border-black/15 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
                   data-testid="notice-library-new-image-add">
              <input type="file" accept="image/*" className="hidden"
                     disabled={uploading}
                     onChange={(e) => handleUpload(e.target.files?.[0])}
                     data-testid="notice-library-new-image-input" />
              <Upload size={11} /> {uploading ? '업로드 중…' : '이미지 추가'}
            </label>
          )}
          <span className="text-[10px] text-gray-400 ml-auto">{draftText.length}/200</span>
          <button type="button" onClick={handleCreate}
                  disabled={busy || (!draftText.trim() && !draftImageUrl)}
                  className="px-3 py-1 rounded-full text-[11px] font-black bg-black text-white dark:bg-white dark:text-black disabled:opacity-40"
                  data-testid="notice-library-new-save">
            <Plus size={11} className="inline mr-1" /> 보관함에 저장
          </button>
        </div>
      </div>

      <div className="space-y-1.5" data-testid="notice-library-list">
        <p className="text-[10px] font-black tracking-widest uppercase text-gray-500 mt-2">저장된 공지 ({items.length}개)</p>
        {loading && <p className="text-[11px] text-gray-400">불러오는 중…</p>}
        {!loading && items.length === 0 && (
          <p className="text-[11px] text-gray-400 py-2" data-testid="notice-library-empty">
            아직 저장된 공지가 없어요. 위에서 자주 쓰는 공지를 저장해 두세요.
          </p>
        )}
        {items.map((n) => {
          const isEditing = editingId === n.notice_id;
          return (
            <div key={n.notice_id}
                 className={`rounded-lg border p-2 flex items-start gap-2 ${activeId && activeId === n.notice_id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}
                 data-testid={`notice-library-item-${n.notice_id}`} data-active={activeId === n.notice_id ? '1' : '0'}>
              {!hideImage && (n.image_url
                ? <img src={n.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                : <div className="w-10 h-10 rounded bg-black/[0.04] dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                    <ImageIcon size={14} className="text-gray-400" />
                  </div>)}
              <div className="flex-1 min-w-0">
                {!isEditing ? (
                  <>
                    <p className="text-[12px] font-semibold text-black dark:text-white break-words line-clamp-2">
                      {n.text || <span className="text-gray-400">(이미지 전용 공지)</span>}
                    </p>
                    <div className="mt-1 flex items-center flex-wrap gap-1">
                      <button type="button"
                              onClick={() => onApply?.(n)}
                              disabled={applyDisabled}
                              className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-[#FF4F3D] text-white hover:bg-[#D92F27] disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`notice-library-apply-${n.notice_id}`}>
                        {applyLabel}
                      </button>
                      <button type="button" onClick={() => beginEdit(n)}
                              className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                              data-testid={`notice-library-edit-${n.notice_id}`}>
                        <Pencil size={10} className="inline mr-0.5" /> 수정
                      </button>
                      <button type="button" onClick={() => remove(n.notice_id)}
                              disabled={busy}
                              className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40"
                              data-testid={`notice-library-remove-${n.notice_id}`}>
                        <Trash2 size={10} className="inline mr-0.5" /> 삭제
                      </button>
                    </div>
                    {applyDisabled && applyHint && (
                      <p className="mt-0.5 text-[10px] text-gray-400">{applyHint}</p>
                    )}
                  </>
                ) : (
                  <div className="space-y-1.5">
                    <textarea value={editText} maxLength={200} rows={2}
                              onChange={(e) => setEditText(e.target.value)}
                              className="w-full px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F] text-[11px] focus:outline-none focus:border-[#FF4F3D]"
                              data-testid={`notice-library-edit-text-${n.notice_id}`} />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {editImageUrl && (
                        <>
                          <img src={editImageUrl} alt="" className="w-7 h-7 rounded object-cover" />
                          <button type="button" onClick={() => setEditImageUrl('')}
                                  className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10">
                            이미지 제거
                          </button>
                        </>
                      )}
                      {!editImageUrl && !hideImage && (
                        <label className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-dashed border-black/15 dark:border-white/15 cursor-pointer">
                          <input type="file" accept="image/*" className="hidden"
                                 onChange={(e) => handleEditUpload(e.target.files?.[0])} />
                          <Upload size={10} /> 이미지 추가
                        </label>
                      )}
                      <button type="button" onClick={handleEditSave}
                              disabled={busy || (!editText.trim() && !editImageUrl)}
                              className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-black text-white dark:bg-white dark:text-black disabled:opacity-40"
                              data-testid={`notice-library-edit-save-${n.notice_id}`}>저장</button>
                      <button type="button" onClick={cancelEdit}
                              className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-black/10 dark:border-white/10"
                              data-testid={`notice-library-edit-cancel-${n.notice_id}`}>취소</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NoticeLibraryCard;
